/**
 * Directive Vue personnalisée : v-ges-tip
 * Enregistrement global dans main.js
 * 
 * ─── UTILISATION DE BASE ────────────────────────────────────────────────────
 *  !!  button est utilisé à titre démonstratif seulement,       !!
 *  !!  la directive peut se trouver sur n'importe quelle balise !!
 * 
 *  Texte simple :
 *      <button v-ges-tip="'Sauvegarder'">...</button>
 * 
 *  Texte réactif (réévalué à chaque ouverture du tooltip) :
 *      <button v-ges-tip="() => btnViewAll">...</button>
 * 
 *  Objet d'options (forme complète) :
 *      <button v-ges-tip="{ content: 'Sauvegarder' }">...</button>
 * 
 * ─── MODIFICATEUR DE PLACEMENT ──────────────────────────────────────────────
 *  
 *  Le premier modificateur définit le placement préféré.
 *  Si le tooltip ne rentre pas, il bascule automatique vers le meilleur côté disponible.
 * 
 *      <span v-ges-tip.top="'Tooltip en haut'">...</span>      ← défaut
 *      <span v-ges-tip.bottom="'Tooltip en bas'">...</span>
 *      <span v-ges-tip.left="'Tooltip à gauche'">...</span>
 *      <span v-ges-tip.right="'Tooltip à droit'">...</span>
 * 
 * ─── OBJET D'OPTIONS COMPLET ────────────────────────────────────────────────
 * 
 *  <button v-ges-tip="{
 *      content:    'Texte du tooltip',     // string | () => string (requis sauf si `condition` est utilisé)
 *      html:       false,                  // rendre le contenu en HTML brut
 *      offset:     10,                     // écart entre le déclencheur et le tooltip (px)
 *      offsetX:    0,                      // décalage horizontal supplémentaire (px)
 *      offsetY:    0,                      // décalage vertical supplémentaire (px)
 *      maxWidth:   300,                    // largeur maximale du tooltip (px)
 *      classes:    ['text-danger'],        // classe(s) CSS supplémentaire(s) sur le tooltip
 *  }">
 * 
 * ─── TOOLTIP CONDITIONNEL ───────────────────────────────────────────────────
 * 
 *  Quand `condition` est fourni, la directive passe en mode "garde" :
 *  
 *  Cas possibles
 *   1. Seulement `condition` est envoyé. (Ce cas remplace l'ancienne directive data-bs-permission de tooltip.js R.I.P. 🪦)
 *       Si true  : Le message par défaut est affiché `Vous n'avez pas la permission pour faire cette action`
 *       Si false : Le tooltip n'apparaît pas
 * 
 *       <button v-ges-tip="{condition: !this.$store.permissionStore.canAccess('can_edit_billing')}">...</button>
 * 
 *   2. `condition` et `conditionContent` sont envoyés.
 *       Si true  : Le contenu de `conditionContent` est affiché à la place du message par défaut de permission
 *       Si false : Le tooltip n'apparaît pas
 * 
 *       <span v-ges-tip="{condition: isFinalPayment, conditionContent: 'Paiement Final'}">
 * 
 *   3. `condition` ET `content` sont envoyés, `conditionContent` → optionnel
 *       Si true  : Si `conditionContent` est envoyé, son contenu est affiché
 *                  Si non, message de permission par défaut affiché
 *       Si false : Le contenu de `content` est affiché dans le tooltip
 * 
 *       button class="btn btn-light btn-sm" @click="openCreditAccounts()" :disabled="!this.$store.permissionStore.canAccess('can_view_credit_accounts')"
 *           v-ges-tip.bottom="{
 *               content: 'Afficher Comptes Entreprises', 
 *               condition: !this.$store.permissionStore.canAccess('can_view_credit_accounts'),
 *           }"    
 *       >
 *           <i class="fa-solid fa-cog"></i>
 *       </button>
 * 
 * ─── CONTENU HTML ───────────────────────────────────────────────────────────
 *  
 *  <span v-ges-tip="{ content: '<b>Texte</b> en gras', html: true }">Survolez-moi</span>
 *  <span v-ges-tip="{ content: getTooltipTextByItem(item), html: true }">Survolez-moi</span>
 * 
 * ─── CLASSES CSS SUPPLÉMENTAIRES ────────────────────────────────────────────
 * 
 *  Utile pour appliquer des variantes de thème définies dans le CSS du component ou du CSS général.
 *  '.tooltip-warning' est inclus dans le component GesTip.vue à titre d'exemple.
 *  Vous pouvez y ajouter d'autres styles si ceux-ci sont seulement pour les tooltips.
 *  
 *  <span v-ges-tip="{ content: 'Danger !', classes: ['tooltip-warning'] }">...</span>
 * 
 * ─── CONTENU RÉACTIF ────────────────────────────────────────────────────────
 * 
 *  Le `content`, `condition` et `conditionContent` peuvent être des fonctions. Ils sont réévalués
 *  de façon réactive via watchEffect pendant que le tooltip est ouvert, donc le tooltip
 *  se met à jour en temps réel si les données associées changent
 * 
 *  <span v-ges-tip="{ content: () => `${count} éléments sélectionnés` }">...</span>
 */

import { showTooltip, hideTooltip, _GESTIP_DEFAULT } from '@/custom/gestip/tooltipService';
import { watchEffect } from 'vue';

// Valeur par défaut à afficher pour les tooltips si aucun autre contenu est envoyé
const DEFAULT_PERMISSION_MSG = `Vous n'avez pas la permission pour faire cette action`;

/**
 * Normalise la valeur du binding de la directive en un objet d'options cohérent.
 * 
 * Formes acceptées pour binding.value :
 *   - Function → { content: fn }
 *   - String   → { content: () => string }
 *   - Object   → utilisé tel quel
 */
function normalize(binding) {
    if (typeof binding.value === 'function') {
        return {content: binding.value};
    }

    if (typeof binding.value === 'string') {
        return {content: () => binding.value};
    }

    return binding.value || {};
}

/**
 * Résout une valeur qui peut être une valeur brute ou une fonction getter.
 * 
 * @param {*|function} value 
 * @returns {*}
 */
function resolve(value) {
    return typeof value === 'function'
        ? value()
        : value;
}

/**
 * Attache le comportement du tooltip à un élément DOM.
 * Appelé lors du `mounted` et réutilisé si nécessaire lors des mises à jour.
 * 
 * Stocke tout sur l'élément lui-même (__opt, __placement, __hide, __stopWatch, __isHovered)
 * pour simplifier le nettoyage dans `unmounted`.
 */
function bindTooltip(el, binding) {
    // Le premier modificateur est le placement préféré | défaut → voir _GESTIP_DEFAULT.placement
    // Ex: v-ges-tip.left → placement = 'left
    const placement = Object.keys(binding.modifiers)[0] || _GESTIP_DEFAULT.placement; 

    el.__opt = normalize(binding);
    el.__placement = placement;

    el.__show = () => {
        const opt = el.__opt;

        const hasCondition = typeof opt.condition === 'boolean' || typeof opt.condition === 'function';
        const conditionResult = resolve(opt.condition);

        // condition === true → affiche le message par défaut ou le contenu de `conditionContent` à la place du contenu normal
        if(hasCondition && conditionResult === true) {
            showTooltip({
                target: el,
                content: () => resolve(opt.conditionContent) || DEFAULT_PERMISSION_MSG,
                placement: el.__placement,
                html: opt.html || _GESTIP_DEFAULT.html,
            });
            return;
        }

        const hasContent = typeof opt.content === 'string' || typeof opt.content === 'function';

        // condition === false && content est vide → n'affiche pas le tooltip
        // Évite d'avoir des tooltips vides ou les gestions d'instances comme les tooltips de Bootstrap
        if (hasCondition && conditionResult === false && !hasContent) { return; }

        // Cas normal → affiche le tooltip avec son contenu
        const contentFn = resolve(opt.content);

        showTooltip({
            target: el,
            content: contentFn,
            placement: el.__placement,
            html: opt.html || _GESTIP_DEFAULT.html,
            offset: opt.offset || _GESTIP_DEFAULT.offset,
            offsetX: opt.offsetX || _GESTIP_DEFAULT.offsetX,
            offsetY: opt.offsetY || _GESTIP_DEFAULT.offsetY,
            maxWidth: opt.maxWidth || _GESTIP_DEFAULT.maxWidth,
            classes: opt.classes || _GESTIP_DEFAULT.classes
        });
    };

    el.__hide = () => hideTooltip();

    /**
     * Watcher réactif : relance __show() dès que les dépendances réactives accessibles
     * à l'intérieur changent (fonction content, condition ou conditionContent).
     * Actif uniquement pendant que le curseur survole l'élément (__isHovered)
     */
    el.__stopWatch = watchEffect(() => {
        const opt = el.__opt;

        const contentResult = resolve(opt.content);
        const conditionResult = resolve(opt.condition);
        const conditionContentResult = resolve(opt.conditionContent);

        if (!el.__isHovered) return;

        let finalContent = '';

        const hasCondition = typeof opt.condition === 'boolean' || typeof opt.condition === 'function';

        if (hasCondition && conditionResult) {
            finalContent = conditionContentResult || DEFAULT_PERMISSION_MSG;
        } else {
            finalContent = contentResult;
        }

        // Si finalContent est vide, c'est que le tooltip ne doit plus afficher
        // condition === false et aucun contenu à afficher
        if (!finalContent) {
            hideTooltip(0);
            return;
        }

        el.__show();
    })

    el.addEventListener('mouseenter', () => {
        el.__isHovered = true;
        el.__show();
    });
    el.addEventListener('mouseleave', () => {
        el.__isHovered = false;
        el.__hide();
    });
}

export default {
    /**
     * Appelé une fois quand l'élément est inséré dans le DOM.
     */
    mounted(el, binding) {
        bindTooltip(el, binding);
    },

    /**
     * Appelé à chaque fois que le composant propriétaire de la directive est re-rendu
     * Seul l'objet d'options est rafraichi, les event listeners ne sont pas ré-attachés.
     */
    updated(el, binding) {
        el.__opt = normalize(binding);
    },

    /**
     * Appelé quand l'élément est retiré du DOM.
     * Nettoie les event listeners et le watcher réactif pour éviter les fuites de mémoire.
     */
    unmounted(el) {
        if (el.__isHovered) el.__hide();
        
        el.removeEventListener('mouseenter', el.__show);
        el.removeEventListener('mouseleave', el.__hide);

        if (el.__stopWatch) el.__stopWatch();
    }
}